import { useSyncExternalStore } from "react";
import logger from "./logger";

export function isDocumentPipSupported(): boolean {
  return typeof window !== "undefined" && !!window.documentPictureInPicture;
}

/**
 * Mirrors the app's stylesheets into the PiP document. Same-origin sheets are
 * inlined rule by rule; a sheet whose rules cannot be read (CORS) is re-linked
 * by URL instead.
 */
export function copyStyles(target: Window): void {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const cssText = Array.from(sheet.cssRules)
        .map((rule) => rule.cssText)
        .join("\n");
      const style = target.document.createElement("style");
      style.textContent = cssText;
      target.document.head.appendChild(style);
    } catch {
      const href = (sheet as CSSStyleSheet).href;
      if (!href) continue;
      const link = target.document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      target.document.head.appendChild(link);
    }
  }
}

/** Carries the light/dark theme across so the card does not flash the wrong palette. */
function copyTheme(target: Window): void {
  const source = document.documentElement;
  target.document.documentElement.className = source.className;
  const theme = source.getAttribute("data-theme");
  if (theme) target.document.documentElement.setAttribute("data-theme", theme);
  target.document.body.style.margin = "0";
  target.document.body.style.background = "#000";
}

export async function openPipWindow(options: {
  width: number;
  height: number;
}): Promise<Window | null> {
  const api = window.documentPictureInPicture;
  if (!api) return null;
  try {
    const pipWindow = await api.requestWindow({
      width: options.width,
      height: options.height,
    });
    copyStyles(pipWindow);
    copyTheme(pipWindow);
    track(pipWindow);
    return pipWindow;
  } catch (err) {
    logger.warn("[PiP] Document picture-in-picture refused", err);
    return null;
  }
}

/**
 * The Document PiP window open now (a tab has at most one), whichever surface opened it: the
 * live card (PipHost) or a popped-out cast (StreamDock). Overlays that must reach a user
 * watching in PiP, such as the keep-alive prompt, portal into its document.
 */
let openWindow: Window | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function track(win: Window): void {
  openWindow = win;
  win.addEventListener("pagehide", () => {
    if (openWindow !== win) return;
    openWindow = null;
    notify();
  });
  notify();
}

export function currentPipWindow(): Window | null {
  return openWindow;
}

export function subscribePipWindow(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The open Document PiP window, or null; re-renders when one opens or closes. */
export function usePipWindow(): Window | null {
  return useSyncExternalStore(subscribePipWindow, currentPipWindow, () => null);
}
